import 'react-datepicker/dist/react-datepicker.css';
import './PunchupDateTimePicker.css';

import DatePicker from 'react-datepicker';
import { FC } from 'react';
import { CalendarDays } from 'lucide-react';
import { useFormContext, get } from 'react-hook-form';
import { PunchupFieldHelp, PunchupFieldLabel } from '../../fields/PunchupFieldLabel';

interface PunchupDateTimePickerProps {
	id: string;
	name: string;
	label?: string;
	required?: boolean;
	/** Optional "explain this field" tooltip beside the label. */
	help?: PunchupFieldHelp;
	onChange?: (date: Date) => void;
	minDate?: Date;
	minTime?: Date;
	maxDate?: Date;
	maxTime?: Date;
}

export const PunchupDateTimePicker: FC<PunchupDateTimePickerProps> = ({
	id,
	name,
	label,
	required,
	help,
	onChange,
	minDate,
	minTime,
	maxDate,
	maxTime,
}) => {
	const {
		formState: { errors, dirtyFields },
		watch,
		setValue,
	} = useFormContext();

	const value = watch(name);
	// get() resolves nested paths like "dates[0].end_date" that a flat key lookup cannot — the
	// cross-field error (end < start) lives at the nested path, so a flat lookup never found it.
	const error = get(errors, name)?.message;
	const isFieldDirty = !!get(dirtyFields, name as string);

	const handleChange = (date: Date) => {
		// shouldValidate on every pick: a date that breaks a cross-field rule turns red the moment it is chosen,
		// and a corrected one clears live. Save re-runs the same check. shouldDirty marks the field changed.
		setValue(name, date, { shouldValidate: true, shouldDirty: true });
		onChange?.(date);
	};

	const parsedValue = value ? new Date(value) : null;

	return (
		<div className={`punchupDateTimeOuter${isFieldDirty && !error ? ' punchupDateTimeDirty' : ''}`}>
			{label && <PunchupFieldLabel label={label} htmlFor={id} required={required} help={help} />}
			<div className={`punchupDateTimeInputContainer${error ? ' hasError' : ''}`}>
				<DatePicker
					id={id}
					name={name}
					selected={parsedValue}
					onChange={handleChange}
					showTimeSelect
					timeIntervals={15}
					dateFormat="MM/dd/yyyy h:mm aa"
					placeholderText="mm/dd/yyyy h:mm"
					timeCaption="Time"
					autoComplete="off"
					className="punchup-datepicker"
					minDate={minDate}
					minTime={minTime}
					maxDate={maxDate}
					maxTime={maxTime}
				/>
				<CalendarDays size={16} className="punchupDateTimeIcon" />
			</div>
			{error && <span className="punchupDateTimeError">{error as string}</span>}
		</div>
	);
};
